import { getColors } from '../../charts/shared/palettes';
import { J, JN, LEGEND_SPACING, TITLE_SPACING, fileHeader } from './shared';

export function genLine(chartType, config, t, data) {
  const groups = {};
  data.forEach(pt => {
    const s = pt.series || 'Series 1';
    if (!groups[s]) groups[s] = [];
    groups[s].push({ x: pt.x, y: pt.y });
  });
  Object.values(groups).forEach(pts => pts.sort((a, b) => a.x - b.x));
  const seriesNames = Object.keys(groups);

  const baseColors = getColors(config, seriesNames.length, t);
  const seriesColors = Object.fromEntries(seriesNames.map((s, i) => [s, baseColors[i % baseColors.length]]));

  const xVals = data.map(d => d.x);
  const yVals = data.map(d => d.y);
  const xDomMin = (config.xMin && String(config.xMin).trim() !== '') ? parseFloat(config.xMin) : Math.min(...xVals);
  const xDomMax = (config.xMax && String(config.xMax).trim() !== '') ? parseFloat(config.xMax) : Math.max(...xVals);
  const yDomMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : Math.floor(Math.min(...yVals) * 0.95);
  const yDomMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(Math.max(...yVals) * 1.05);

  const showGrid = !!config.showGrid;
  const showMarkers = !!config.showMarkers;
  const showLegend = !!config.showLegend;
  const hasTitle = !!(config.title && String(config.title).trim());
  const legendPos = config.legendPosition || 'top';
  const lineWidth = config.lineWidth || 2;
  const markerSize = config.markerSize || 6;

  const marginTop = hasTitle ? 40 : 20;
  const marginRight = legendPos === 'right' ? 150 : 80;
  const marginBottom = legendPos === 'bottom' ? 80 : 60;

  const gridBlock = showGrid
    ? `          <GridRows scale={yScale} width={innerWidth} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />
          <GridColumns scale={xScale} height={innerHeight} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />`
    : '';

  const markerBlock = showMarkers
    ? `          {SERIES_NAMES.map(series =>
            SERIES_DATA[series].map((d, i) => (
              <circle key={series + '-' + i} cx={xScale(d.x)} cy={yScale(d.y)} r={${markerSize}} fill={SERIES_COLORS[series]} />
            ))
          )}`
    : '';

  const legendTopBlock = (showLegend && legendPos === 'top')
    ? `          <g transform={'translate(' + (innerWidth / 2) + ', -${LEGEND_SPACING.topOffset})'}>
            {SERIES_NAMES.map((series, i) => {
              const startX = -(SERIES_NAMES.length * 110) / 2 + i * 110;
              return (
                <g key={series} transform={'translate(' + startX + ', 0)'}>
                  <line x1={0} y1={0} x2={${LEGEND_SPACING.lineLength}} y2={0} stroke={SERIES_COLORS[series]} strokeWidth={2} />
                  <text x={${LEGEND_SPACING.lineTextX}} y={${LEGEND_SPACING.textDy}} fill={THEME.legendTextColor} fontSize={11}>{series}</text>
                </g>
              );
            })}
          </g>`
    : '';

  const legendBottomBlock = (showLegend && legendPos === 'bottom')
    ? `          <g transform={'translate(' + (innerWidth / 2) + ', ' + (innerHeight + ${LEGEND_SPACING.bottomGap}) + ')'}>
            {SERIES_NAMES.map((series, i) => {
              const startX = -(SERIES_NAMES.length * 110) / 2 + i * 110;
              return (
                <g key={series} transform={'translate(' + startX + ', 0)'}>
                  <line x1={0} y1={0} x2={${LEGEND_SPACING.lineLength}} y2={0} stroke={SERIES_COLORS[series]} strokeWidth={2} />
                  <text x={${LEGEND_SPACING.lineTextX}} y={${LEGEND_SPACING.textDy}} fill={THEME.legendTextColor} fontSize={11}>{series}</text>
                </g>
              );
            })}
          </g>`
    : '';

  const legendRightBlock = (showLegend && legendPos === 'right')
    ? `        <g transform={'translate(' + (${marginTop} + innerWidth + ${LEGEND_SPACING.rightGap}) + ', ' + (${marginTop} + innerHeight / 2 - SERIES_NAMES.length * 12) + ')'}>
          {SERIES_NAMES.map((series, i) => (
            <g key={series} transform={'translate(0, ' + (i * ${LEGEND_SPACING.rightItemHeight}) + ')'}>
              <line x1={0} y1={0} x2={${LEGEND_SPACING.lineLength}} y2={0} stroke={SERIES_COLORS[series]} strokeWidth={2} />
              <text x={${LEGEND_SPACING.lineTextX}} y={${LEGEND_SPACING.textDy}} fill={THEME.legendTextColor} fontSize={11}>{series}</text>
            </g>
          ))}
        </g>`
    : '';

  return (
    fileHeader(['@visx/shape', '@visx/scale', '@visx/axis', '@visx/grid', '@visx/group'], 'line-chart') +
    `import React, { useMemo } from 'react';
import { LinePath } from '@visx/shape';
import { scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { Group } from '@visx/group';

const SERIES_DATA = ${JN(groups)};
const SERIES_COLORS = ${JN(seriesColors)};
const SERIES_NAMES = ${J(seriesNames)};
const THEME = ${JN(t)};
const X_DOMAIN = [${xDomMin}, ${xDomMax}];
const Y_DOMAIN = [${yDomMin}, ${yDomMax}];

export default function Chart({ width = 720, height = 480 }) {
  const marginLeft = 70;
  const marginTop = ${marginTop};
  const marginRight = ${marginRight};
  const marginBottom = ${marginBottom};
  const innerWidth = Math.max(width - marginLeft - marginRight, 0);
  const innerHeight = Math.max(height - marginTop - marginBottom, 0);

  const xScale = useMemo(() => scaleLinear({ domain: X_DOMAIN, range: [0, innerWidth] }), [innerWidth]);
  const yScale = useMemo(() => scaleLinear({ domain: Y_DOMAIN, range: [innerHeight, 0] }), [innerHeight]);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        <Group left={marginLeft} top={marginTop}>
${gridBlock}
          {SERIES_NAMES.map(series => (
            <LinePath
              key={series}
              data={SERIES_DATA[series]}
              x={d => xScale(d.x)}
              y={d => yScale(d.y)}
              stroke={SERIES_COLORS[series]}
              strokeWidth={${lineWidth}}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
${markerBlock}
          <AxisBottom
            scale={xScale}
            top={innerHeight}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'middle', fontFamily: THEME.fontBody })}
            label={${J(config.xlabel || '')}}
            labelProps={{ fill: THEME.labelColor, fontSize: 14, textAnchor: 'middle', fontFamily: THEME.fontBody }}
            labelOffset={20}
          />
          <AxisLeft
            scale={yScale}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke={THEME.axisColor}
            tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, textAnchor: 'end', dx: '-0.25em', dy: '0.33em', fontFamily: THEME.fontBody })}
            label={${J(config.ylabel || '')}}
            labelProps={{ fill: THEME.labelColor, fontSize: 14, textAnchor: 'middle', fontFamily: THEME.fontBody }}
            labelOffset={45}
          />
${legendTopBlock}
${legendBottomBlock}
        </Group>
${legendRightBlock}
      </svg>
    </div>
  );
}
`
  );
}
